import React, {ReactElement, useEffect, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import '../../../../../../assets/grid.css';
import {useTranslation} from 'react-i18next';
import {DeviceType} from '../../../../../incase-domain/common/DeviceType.enum';
import {IElementQuestionYesNo} from '../../../../../incase-domain/elements/question/questionYesNo';
import {YesNoElementInteractions} from '../../../../../incase-domain/proptypes/runfile';
import {jumpToSlideWithIdAction} from '../../../../../store/actions/presentation.actions';
import {activeMobileQuestionSelector, slideIndexListSelector} from '../../../../../store/selectors/presentation.selectors';
import QuestionLayout from '../common/QuestionLayout';
import QuestionAnswer, {AnswerTypes} from '../question/QuestionAnswer';
import {getAnswerStyles} from '../common/getStyles';
import useYesNoQuestionActions from './useYesNoQuestionTry';
import {useOpenComment} from '../common/useOpenComment';
import {useSlidePreviewCapture} from '../../../../../context/SlidePreviewCaptureContext';

interface YesNoQuestionElementProps {
    element: IElementQuestionYesNo;
    elementInteractions: YesNoElementInteractions;
    device?: DeviceType;
}

function YesNoQuestionElement(props: YesNoQuestionElementProps): ReactElement {
    const dispatch = useDispatch();
    const {t} = useTranslation();

    const slideIndexList = useSelector(slideIndexListSelector);
    const activeMobileQuestion = useSelector(activeMobileQuestionSelector);

    const {element, elementInteractions, device = DeviceType.DESKTOP} = props;
    const {slideChange} = element;

    const {selectAnswer, confirmAnswer} = useYesNoQuestionActions();
    const {getCommentProps, toggleComment} = useOpenComment();
    const isSlidePreviewCapture = useSlidePreviewCapture();

    const interact = useRef(false);
    useEffect(() => {
        if (interact.current && slideIndexList && slideChange[elementInteractions.answer]?.slideId) {
            const {slideId} = slideChange[elementInteractions.answer];
            dispatch(jumpToSlideWithIdAction(slideId, true));
        }
    }, [interact, slideIndexList, elementInteractions.answered, slideChange, dispatch, elementInteractions.answer]);

    const handleMarkAnswer = (val: number): void => {
        let markedAnswer: number | undefined = elementInteractions.answer;
        if (markedAnswer === val) markedAnswer = undefined;
        else markedAnswer = val;
        selectAnswer(element, elementInteractions, markedAnswer);
    };

    const handleConfirmAnswer = (): void => {
        interact.current = true;
        confirmAnswer(element, elementInteractions);
    };

    const isAnswerSelected = (index: number): boolean => elementInteractions.answer === index;

    const getAnswerType = (index: number): AnswerTypes => {
        if (elementInteractions.answered) {
            if (element.isDebatable) {
                return AnswerTypes.DEBATABLE;
            }
            if (element.correct && index === 1 && isAnswerSelected(1)) {
                return AnswerTypes.CORRECT;
            }
            if (element.correct && index === 0 && isAnswerSelected(0)) {
                return AnswerTypes.INCORRECT;
            }
        }
        return isAnswerSelected(index) ? AnswerTypes.SELECTED : AnswerTypes.NONE;
    };

    return (
        <QuestionLayout
            element={element}
            elementInteractions={elementInteractions}
            device={device}
            showConfirmButton
            onConfirm={() => handleConfirmAnswer()}
            confirmDisabled={elementInteractions.answered || elementInteractions.answer === undefined}
        >
            <div
                className={`c-yesNoQuestion ${device === DeviceType.MOBILE && 'c-yesNoQuestion--mobile'} ${
                    elementInteractions.answered && 'c-yesNoQuestion--answered'
                } ${activeMobileQuestion === element.id && 'is-active'}`}
            >
                <QuestionAnswer
                    key={`question-answer-${element.id}-${1}`}
                    answer={t(`common.yes`)}
                    answerType={getAnswerType(1)}
                    comment={
                        isSlidePreviewCapture || (elementInteractions.answered && (isAnswerSelected(1) || element.showAllComments))
                            ? element.comment.yes
                            : undefined
                    }
                    correctColor={element.correctColor}
                    incorrectColor={element.incorrectColor}
                    debatableColor={element.debatableColor}
                    styles={getAnswerStyles(device, element)}
                    onClick={() => {
                        handleMarkAnswer(1);
                        toggleComment(
                            1,
                            !!element.comment.yes && elementInteractions.answered && (isAnswerSelected(1) || element.showAllComments)
                        );
                    }}
                    device={device}
                    {...getCommentProps(
                        1,
                        !!element.comment.yes && elementInteractions.answered && (isAnswerSelected(1) || element.showAllComments)
                    )}
                />
                <QuestionAnswer
                    key={`question-answer-${element.id}-${0}`}
                    answer={t(`common.no`)}
                    answerType={getAnswerType(0)}
                    comment={
                        isSlidePreviewCapture || (elementInteractions.answered && (isAnswerSelected(0) || element.showAllComments))
                            ? element.comment.no
                            : undefined
                    }
                    correctColor={element.correctColor}
                    incorrectColor={element.incorrectColor}
                    debatableColor={element.debatableColor}
                    styles={getAnswerStyles(device, element)}
                    onClick={() => {
                        handleMarkAnswer(0);
                        toggleComment(
                            0,
                            !!element.comment.no && elementInteractions.answered && (isAnswerSelected(0) || element.showAllComments)
                        );
                    }}
                    device={device}
                    {...getCommentProps(
                        0,
                        !!element.comment.no && elementInteractions.answered && (isAnswerSelected(0) || element.showAllComments)
                    )}
                />
            </div>
        </QuestionLayout>
    );
}

export default YesNoQuestionElement;
